import React, { useState } from 'react';
import {
	ActivityIndicator,
	GestureResponderEvent,
	StyleProp,
	StyleSheet,
	Text,
	TextStyle,
	View,
	ViewStyle,
} from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import { useSvgId } from '../../../utils/hooks/useSvgId';
import { Pressable } from './Touchable';

const CTA_GRADIENT_STOPS: Record<'gold' | 'success', ReadonlyArray<readonly [string, string]>> = {
	gold: [
		['0', '#FFF5B9'],
		['0.35', '#FFEE82'],
		['0.78', '#F5A300'],
		['1', '#CE7C00'],
	],
	success: [
		['0', '#66D93D'],
		['0.55', '#248F1C'],
		['1', '#0D540F'],
	],
};

type Props = {
	label: string;
	onPress?: (event: GestureResponderEvent) => void;
	disabled?: boolean;
	busy?: boolean;
	accessibilityLabel?: string;
	icon?: React.ReactNode;
	variant?: 'success' | 'danger' | 'gold';
	size?: 'small' | 'medium';
	/** Shows a red dot to indicate new content. */
	showNotificationDot?: boolean;
	style?: StyleProp<ViewStyle>;
	surfaceStyle?: StyleProp<ViewStyle>;
	/** For a host drawn at a scale, so the label grows with the button. */
	labelStyle?: StyleProp<TextStyle>;
};

export const CtaButton = ({
	label,
	onPress,
	disabled = false,
	busy = false,
	accessibilityLabel,
	icon,
	variant = 'success',
	size = 'small',
	showNotificationDot = false,
	style,
	surfaceStyle,
	labelStyle,
}: Props) => {
	const [surfaceSize, setSurfaceSize] = useState({ height: 0, width: 0 });
	const gradientId = useSvgId('ctaButtonGradient');
	const gradientStops = CTA_GRADIENT_STOPS[variant === 'gold' ? 'gold' : 'success'];

	return (
		<Pressable
			accessibilityRole='button'
			accessibilityLabel={accessibilityLabel ?? label}
			accessibilityState={{ disabled: disabled || busy, busy }}
			disabled={disabled || busy}
			onPress={onPress}
			style={[
				styles.button,
				!disabled && variant === 'success' && styles.successButton,
				!disabled && variant === 'danger' && styles.dangerButton,
				style,
			]}
		>
			<View
				pointerEvents='none'
				onLayout={({ nativeEvent: { layout } }) => {
					if (surfaceSize.width !== layout.width || surfaceSize.height !== layout.height) {
						setSurfaceSize({ height: layout.height, width: layout.width });
					}
				}}
				style={[
					styles.surface,
					size === 'medium' && styles.mediumSurface,
					variant === 'danger' && styles.dangerSurface,
					variant === 'gold' && styles.goldSurface,
					surfaceStyle,
					(disabled || busy) && styles.disabledSurface,
				]}
			>
				{(variant === 'success' || variant === 'gold') &&
				surfaceSize.width > 0 &&
				surfaceSize.height > 0 ? (
					<Svg
						pointerEvents='none'
						style={[styles.gradient, (disabled || busy) && styles.disabledGradient]}
						width={surfaceSize.width}
						height={surfaceSize.height}
					>
						<Defs>
							<LinearGradient id={gradientId} x1='0' y1='0' x2='0' y2='1'>
								{gradientStops.map(([offset, color]) => (
									<Stop key={offset} offset={offset} stopColor={color} />
								))}
							</LinearGradient>
						</Defs>
						<Rect
							width={surfaceSize.width}
							height={surfaceSize.height}
							fill={`url(#${gradientId})`}
						/>
					</Svg>
				) : null}
				{busy ? (
					<ActivityIndicator color={variant === 'gold' ? '#2A0A04' : '#FFFFFF'} />
				) : icon ? (
					<View style={styles.content}>
						{icon}
						<Text
							numberOfLines={1}
							style={[
								styles.label,
								variant === 'danger' && styles.dangerLabel,
								variant === 'gold' && styles.goldLabel,
								labelStyle,
							]}
						>
							{label}
						</Text>
					</View>
				) : (
					<Text
						numberOfLines={1}
						style={[
							styles.label,
							variant === 'danger' && styles.dangerLabel,
							variant === 'gold' && styles.goldLabel,
							labelStyle,
						]}
					>
						{label}
					</Text>
				)}
			</View>
			{showNotificationDot ? <View pointerEvents='none' style={styles.notificationDot} /> : null}
		</Pressable>
	);
};

const styles = StyleSheet.create({
	button: {
		borderRadius: 8,
		position: 'relative',
	},
	successButton: {
		boxShadow: '0px 0px 10px 0px #40FF3359',
		elevation: 5,
		shadowColor: '#40FF33',
		shadowOffset: { width: 0, height: 0 },
		shadowOpacity: 0.35,
		shadowRadius: 7,
	},
	surface: {
		alignItems: 'center',
		backgroundColor: 'transparent',
		borderColor: '#BEFF8B',
		borderRadius: 8,
		borderWidth: 1,
		flex: 1,
		justifyContent: 'center',
		minHeight: 32,
		overflow: 'hidden',
		paddingHorizontal: 12,
	},
	mediumSurface: { minHeight: 44 },
	gradient: { ...StyleSheet.absoluteFill },
	disabledGradient: { opacity: 0 },
	dangerButton: {
		boxShadow: '0px 0px 10px 0px #FF4D3D59',
		elevation: 5,
		shadowColor: '#FF4D3D',
		shadowOpacity: 0.35,
		shadowRadius: 7,
	},
	dangerSurface: { backgroundColor: '#3B0C05', borderColor: '#C76A1F' },
	dangerLabel: { color: '#FFF5B9' },
	goldSurface: { borderColor: '#FFE45C' },
	goldLabel: { color: '#2A0A04' },
	disabledSurface: { backgroundColor: '#465040', borderColor: 'rgba(184,255,140,0.25)' },
	content: { alignItems: 'center', columnGap: 8, flexDirection: 'row' },
	label: { color: '#FFFFFF', fontFamily: 'Sofia Sans', fontSize: 13, fontWeight: '700' },
	notificationDot: {
		backgroundColor: '#ED2B2A',
		borderColor: '#FFFFFF',
		borderRadius: 6,
		borderWidth: 1,
		height: 12,
		position: 'absolute',
		right: -4,
		top: -4,
		width: 12,
	},
});

export default CtaButton;
